import type { ValidationItem } from '@/services/projections'

export type CollapsedValidationRow = {
  key: string
  code: string
  view: string | null
  line_key: string | null
  label?: string | null
  message: string
  monthRange: string | null
  count: number
}

function collapseKey(item: ValidationItem): string {
  return `${item.code}\0${item.view ?? ''}\0${item.line_key ?? ''}`
}

export function formatMonthRanges(ts: number[]): string {
  const sorted = [...new Set(ts)].sort((a, b) => a - b)
  const parts: string[] = []
  let i = 0
  while (i < sorted.length) {
    const start = sorted[i]
    if (start == null) break
    let j = i
    while (j + 1 < sorted.length && sorted[j + 1] === (sorted[j] ?? start) + 1) {
      j += 1
    }
    const end = sorted[j] ?? start
    if (j === i) {
      parts.push(`M+${start}`)
    } else {
      parts.push(`M+${start}–M+${end}`)
    }
    i = j + 1
  }
  return parts.join(', ')
}

function baseMessage(message: string, hasMonths: boolean): string {
  if (!hasMonths) return message
  return message.replace(/\s+at t=\d+$/, '')
}

export function collapseValidationItems(items: ValidationItem[]): CollapsedValidationRow[] {
  const groups = new Map<string, ValidationItem[]>()
  for (const item of items) {
    const key = collapseKey(item)
    const list = groups.get(key) ?? []
    list.push(item)
    groups.set(key, list)
  }

  return Array.from(groups.entries()).flatMap(([key, groupItems]) => {
    const first = groupItems[0]
    if (!first) return []
    const monthTs = groupItems.map((item) => item.t).filter((t): t is number => t != null)
    const monthRange = monthTs.length > 0 ? formatMonthRanges(monthTs) : null
    return [{
      key,
      code: first.code,
      view: first.view,
      line_key: first.line_key,
      label: first.label,
      message: baseMessage(first.message, monthRange != null),
      monthRange,
      count: groupItems.length,
    }]
  })
}

export function formatCollapsedValidationRow(row: CollapsedValidationRow): string {
  const parts: string[] = []
  if (row.label) {
    parts.push(`${row.label}: `)
  }
  parts.push(row.message)
  if (row.monthRange) {
    parts.push(` (${row.monthRange})`)
  }
  if (row.count > 1) {
    parts.push(` ×${row.count}`)
  }
  return parts.join('')
}
